// BlogGrid.jsx - ENHANCED VERSION WITH SEARCH, FILTERS, PAGINATION

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogCard from './BlogCard';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import { newsletterApi, blogApi } from '../../services/api';
import { getMediaUrl } from '../../utils/environment.js';
import LoadingSpinner, { LoadingSkeleton } from '../common/LoadingSpinner';

const ITEMS_PER_PAGE = 9;

const BlogGrid = () => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({ type: '', message: '' });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [allArticles, setAllArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [loading, setLoading] = useState({
    featured: true,
    all: true,
    categories: true,
  });

  const getImageUrl = (blog) => {
    if (!blog) return '/default-blog.jpg';
    
    if (blog.featured_image_url) {
      return getMediaUrl(blog.featured_image_url);
    }
    
    if (blog.featured_image) {
      if (typeof blog.featured_image === 'string') {
        return getMediaUrl(blog.featured_image);
      }
      if (blog.featured_image.url) {
        return getMediaUrl(blog.featured_image.url);
      }
    }
    
    return '/default-blog.jpg';
  };

  useEffect(() => {
    const loadBlogData = async () => {
      try {
        setLoading({
          featured: true,
          all: true,
          categories: true,
        });

        const allArticlesData = await blogApi.getArticles();
        const allArticlesArray = Array.isArray(allArticlesData) ? allArticlesData : [];
        setAllArticles(allArticlesArray);
        setFilteredArticles(allArticlesArray);

        setFeaturedArticles(allArticlesArray.filter(article => article.is_featured).slice(0, 2));
        
        try {
          const categoriesData = await blogApi.getCategories();
          let categoriesArray = [];
          
          if (Array.isArray(categoriesData)) {
            categoriesArray = categoriesData;
          } else if (categoriesData && typeof categoriesData === 'object') {
            if (categoriesData.results && Array.isArray(categoriesData.results)) {
              categoriesArray = categoriesData.results;
            } else if (Array.isArray(categoriesData.data)) {
              categoriesArray = categoriesData.data;
            }
          }
          
          setCategories(categoriesArray.filter(cat => cat.is_active !== false));
        } catch (error) {
          console.error('Error fetching categories:', error);
          const uniqueCategories = [];
          const seen = new Set();
          allArticlesArray.forEach(article => {
            if (article.category) {
              const category = article.category;
              const key = category.slug || category.name || category;
              if (!seen.has(key)) {
                seen.add(key);
                uniqueCategories.push({
                  id: category.id || key,
                  name: category.name || category,
                  slug: category.slug || key,
                  is_active: true
                });
              }
            }
          });
          setCategories(uniqueCategories);
        }
        
      } catch (error) {
        console.error('Error loading blog data:', error);
        setFeaturedArticles([]);
        setAllArticles([]);
        setFilteredArticles([]);
        setCategories([]);
      } finally {
        setLoading({
          featured: false,
          all: false,
          categories: false,
        });
      }
    };

    loadBlogData();
  }, []);

  // Filter and sort logic
  useEffect(() => {
    let filtered = [...allArticles];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title?.toLowerCase().includes(query) ||
        article.excerpt?.toLowerCase().includes(query) ||
        (typeof article.category === 'object' && article.category.name?.toLowerCase().includes(query)) ||
        (typeof article.category === 'string' && article.category.toLowerCase().includes(query))
      );
    }

    // Apply category filter
    if (activeCategory !== 'all') {
      filtered = filtered.filter(article => {
        if (typeof article.category === 'string') {
          return article.category.toLowerCase() === activeCategory.toLowerCase();
        }
        if (typeof article.category === 'object') {
          return article.category.slug === activeCategory || 
                 article.category.name?.toLowerCase() === activeCategory.toLowerCase();
        }
        return false;
      });
    }

    // Apply sorting
    if (sortBy === 'latest') {
      filtered.sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date));
    } else if (sortBy === 'mostRead') {
      filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    } else if (sortBy === 'trending') {
      filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    }

    setFilteredArticles(filtered);
    setCurrentPage(1);
  }, [allArticles, searchQuery, activeCategory, sortBy]);

  // Pagination logic
  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentArticles = filteredArticles.slice(startIndex, endIndex);
  const recentArticles = filteredArticles.slice(0, 6);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubscribeStatus({ type: 'error', message: 'Please enter your email address' });
      return;
    }

    setIsSubscribing(true);
    
    try {
      await newsletterApi.subscribe(email);
      setSubscribeStatus({ 
        type: 'success', 
        message: 'Successfully subscribed!' 
      });
      setEmail('');
      setShowSubscribePopup(true);
      
      setTimeout(() => {
        setShowSubscribePopup(false);
        setSubscribeStatus({ type: '', message: '' });
      }, 5000);
      
    } catch (error) {
      setSubscribeStatus({ 
        type: 'error', 
        message: error.message || 'Failed to subscribe. Please try again.' 
      });
      
      setTimeout(() => {
        setSubscribeStatus({ type: '', message: '' });
      }, 5000);
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleImageError = (e) => {
    e.target.src = '/default-blog.jpg';
    e.target.onerror = null;
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleCategoryFilter = (categorySlug) => {
    setActiveCategory(categorySlug);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return (
      <div className="enhanced-blog-container">
        <div className="enhanced-blog-header">
          <LoadingSkeleton type="text" count={1} />
        </div>
        <div className="enhanced-blog-layout">
          <main className="enhanced-blog-main">
            <LoadingSkeleton type="card" count={6} />
          </main>
          <aside className="enhanced-blog-sidebar">
            <LoadingSkeleton type="card" count={2} />
          </aside>
        </div>
      </div>
    );
  }

  const safeCategories = Array.isArray(categories) ? categories : [];
  const mostReadArticles = [...allArticles].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5);
  const editorsPicks = [...allArticles].filter(a => a.is_featured || a.editor_pick).slice(0, 4);

  return (
    <div className="enhanced-blog-container">
      {/* Success Popup */}
      {showSubscribePopup && (
        <div className="subscribe-popup-overlay" onClick={() => setShowSubscribePopup(false)}>
          <div className="subscribe-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">✉️</div>
            <h3>Successfully Subscribed!</h3>
            <p>Thank you for subscribing to our newsletter. You'll receive the latest blog posts and career tips directly in your inbox.</p>
            <button 
              className="popup-close-btn"
              onClick={() => setShowSubscribePopup(false)}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="enhanced-blog-header">
        <div className="enhanced-blog-header-content">
          <h1 className="enhanced-blog-title">Career Advice & Resume Tips Blog</h1>
          <SearchBar 
            articles={allArticles}
            categories={safeCategories}
            onSearch={handleSearch}
          />
        </div>

        {/* Category Filters */}
        <div className="enhanced-blog-filters">
          <div className="category-filters">
            <button
              className={`category-filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryFilter('all')}
            >
              All
            </button>
            {safeCategories.map((category) => (
              <button
                key={category.id || category.slug}
                className={`category-filter-btn ${activeCategory === category.slug ? 'active' : ''}`}
                onClick={() => handleCategoryFilter(category.slug)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Sort Dropdown */}
          <div className="sort-dropdown">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="latest">Latest</option>
              <option value="mostRead">Most Read</option>
              <option value="trending">Trending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="enhanced-blog-layout">
        {/* Main Content */}
        <main className="enhanced-blog-main">
          {/* Featured Articles */}
          {featuredArticles.length > 0 && !searchQuery && activeCategory === 'all' && (
            <section className="enhanced-featured-section">
              <h2 className="enhanced-section-title">Featured Articles</h2>
              <div className="enhanced-featured-grid">
                {featuredArticles.map((blog) => (
                  <Link 
                    to={`/blog/${blog.slug || blog.id}`} 
                    key={blog.id} 
                    className="enhanced-featured-card"
                  >
                    <div className="enhanced-featured-image-wrapper">
                      <img 
                        src={getImageUrl(blog)} 
                        alt={blog.title || 'Featured article'} 
                        onError={handleImageError}
                        className="enhanced-featured-image"
                      />
                      <span className="featured-badge">Featured</span>
                      {blog.category && (
                        <span className="enhanced-featured-category">
                          {typeof blog.category === 'object' ? blog.category.name : blog.category}
                        </span>
                      )}
                    </div>
                    <div className="enhanced-featured-content">
                      <h3 className="enhanced-featured-title">
                        {blog.title || 'Untitled Article'}
                      </h3>
                      <p className="enhanced-featured-excerpt">
                        {blog.excerpt ? 
                          blog.excerpt.replace(/<[^>]*>/g, '').substring(0, 150) + '...' : 
                          'No excerpt available'
                        }
                      </p>
                      <div className="enhanced-featured-meta">
                        <div className="enhanced-featured-author">
                          <div className="enhanced-featured-avatar">
                            {(blog.author || 'U').charAt(0).toUpperCase()}
                          </div>
                          <span>{blog.author || 'Unknown Author'}</span>
                        </div>
                        <div className="enhanced-featured-stats">
                          <span>{blog.publish_date ? new Date(blog.publish_date).toLocaleDateString() : 'No date'}</span>
                          <span>•</span>
                          <span>{blog.read_time || '5 min read'}</span>
                          {blog.views > 0 && (
                            <>
                              <span>•</span>
                              <span>👁 {blog.views}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Recent Articles */}
          {!searchQuery && activeCategory === 'all' && recentArticles.length > 0 && (
            <section className="enhanced-articles-section">
              <h2 className="enhanced-section-title">Recent</h2>
              <div className="enhanced-articles-grid">
                {recentArticles.map((blog) => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </div>
            </section>
          )}

          {/* All Articles / Filtered Results */}
          <section className="enhanced-articles-section">
            <h2 className="enhanced-section-title">
              {searchQuery ? `Search Results for "${searchQuery}"` : 
               activeCategory !== 'all' ? `${safeCategories.find(c => c.slug === activeCategory)?.name || 'Category'}` : 
               'All Articles'}
            </h2>
            
            {currentArticles.length > 0 ? (
              <>
                <div className="enhanced-articles-grid">
                  {currentArticles.map((blog) => (
                    <BlogCard key={blog.id} blog={blog} />
                  ))}
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </>
            ) : (
              <div className="no-results">
                <p>No articles found. Try adjusting your search or filters.</p>
              </div>
            )}
          </section>
        </main>

        {/* Sidebar */}
        <aside className="enhanced-blog-sidebar">
          {/* Newsletter */}
          <div className="enhanced-sidebar-card newsletter-card">
            <h3>Stay Updated</h3>
            <p>Get weekly career tips and resume advice</p>
            <form className="enhanced-newsletter-form" onSubmit={handleSubscribe}>
              <input 
                type="email" 
                placeholder="Your email address" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubscribing}
                required
              />
              <button type="submit" disabled={isSubscribing}>
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {subscribeStatus.message && !showSubscribePopup && (
              <div className={`subscribe-message ${subscribeStatus.type}`}>
                {subscribeStatus.message}
              </div>
            )}
          </div>

          {/* Popular Tags */}
          {safeCategories.length > 0 && (
            <div className="enhanced-sidebar-card">
              <h3 className="enhanced-sidebar-title">Popular Tags</h3>
              <div className="enhanced-tags-list">
                {safeCategories.slice(0, 10).map((category) => (
                  <button
                    key={category.id || category.slug}
                    className="enhanced-tag-pill"
                    onClick={() => handleCategoryFilter(category.slug)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Editor's Picks */}
          {editorsPicks.length > 0 && (
            <div className="enhanced-sidebar-card">
              <h3 className="enhanced-sidebar-title">Editor's Picks</h3>
              <div className="enhanced-sidebar-list">
                {editorsPicks.map((blog) => (
                  <Link 
                    to={`/blog/${blog.slug || blog.id}`} 
                    key={blog.id}
                    className="enhanced-sidebar-item"
                  >
                    <img 
                      src={getImageUrl(blog)} 
                      alt={blog.title || 'Blog post'}
                      onError={handleImageError}
                      className="enhanced-sidebar-thumb"
                    />
                    <div className="enhanced-sidebar-item-content">
                      <h4>{blog.title || 'Untitled'}</h4>
                      <span>{blog.read_time || '5 min read'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Most Read */}
          {mostReadArticles.length > 0 && (
            <div className="enhanced-sidebar-card">
              <h3 className="enhanced-sidebar-title">Most Read This Week</h3>
              <div className="enhanced-ranked-list">
                {mostReadArticles.map((blog, index) => (
                  <Link 
                    to={`/blog/${blog.slug || blog.id}`} 
                    key={blog.id}
                    className="enhanced-ranked-item"
                  >
                    <span className="enhanced-rank">{index + 1}</span>
                    <div className="enhanced-ranked-content">
                      <h4>{blog.title || 'Untitled'}</h4>
                      <span>👁 {blog.views || 0} views</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA Box */}
          <div className="enhanced-sidebar-card cta-card">
            <h3>Download Free Resume Template</h3>
            <p>Get our professionally designed resume template to stand out from the crowd.</p>
            <Link to="/templates" className="cta-button">
              Download Now
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogGrid;